import { useState } from 'react'

import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { useTranslation } from 'react-i18next'

import { CrudModalLayout } from '~/components/common/CrudModalLayout'
import { useCrudModalForm } from '~/hooks/useCrudModalForm'
import { PlantUnitFormFields } from '~/pages/AdminPlantUnitMaster/components/PlantUnitFormFields'
import type { PendingSite } from '~/pages/AdminPlantUnitMaster/components/SiteSelectField'
import { useCreatePlantUnit } from '~/pages/AdminPlantUnitMaster/hooks/useCreatePlantUnit'
import {
  useLivePowerTypeOverlapCheck,
  usePlantUnitFormSchema,
} from '~/pages/AdminPlantUnitMaster/hooks/usePowerTypeOverlapCheck'
import { selectionsToClearOnAreaChange } from '~/pages/AdminPlantUnitMaster/models/unitCandidates'
import {
  PENDING_SITE_ID,
  type PlantUnitFormInput,
  type PlantUnitFormValues,
  defaultPlantUnitFormInput,
  plantUnitAttributesFromValues,
} from '~/pages/AdminPlantUnitMaster/models/unitForm'
import type { PlantUnitRow } from '~/pages/AdminPlantUnitMaster/models/unitRows'
import {
  PLANT_UNIT_SAVE_ERROR_KEYS,
  PLANT_UNIT_UNKNOWN_ERROR_KEY,
  resolvePlantUnitSaveError,
} from '~/pages/AdminPlantUnitMaster/models/unitServerError'
import { showSiteAreaMismatchToast } from '~/pages/AdminPlantUnitMaster/models/unitToast'

type SiteOption = { id: string; siteName: string; areaCode: AreaCode }
type PlantOption = {
  id: string
  plantCode: string
  plantName: string
  areaCode: AreaCode
}

type Props = {
  isOpen: boolean
  onClose: () => void
  sites: readonly SiteOption[]
  plants: readonly PlantOption[]
  /** 組織のユニット一覧 (入力中の E1: 同じ系統コードの他のユニットと突き合わせる)。 */
  units: readonly PlantUnitRow[]
}

/** 電源ユニットの登録ダイアログ。発電所はその場で追加でき、ユニットの保存と一緒に作られる。 */
export const CreatePlantUnitModal = ({
  isOpen,
  onClose,
  sites,
  plants,
  units,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { mutateAsync, isPending } = useCreatePlantUnit()
  const [pendingSite, setPendingSite] = useState<PendingSite | undefined>(
    undefined,
  )

  const siteNameOf = (siteId: string): string =>
    siteId === PENDING_SITE_ID
      ? (pendingSite?.siteName ?? '')
      : (sites.find((site) => site.id === siteId)?.siteName ?? '')

  const schema = usePlantUnitFormSchema({ units, plants })
  const {
    control,
    errors,
    serverError,
    handleClose,
    handleSave,
    setValue,
    getValues,
    trigger,
    isDirty,
    isSubmitting,
  } = useCrudModalForm<PlantUnitFormInput, PlantUnitFormValues>({
    schema,
    defaultValues: defaultPlantUnitFormInput(),
    onSubmit: async (values) => {
      const attributes = plantUnitAttributesFromValues(values)
      const isNewSite = values.siteId === PENDING_SITE_ID
      if (isNewSite && pendingSite === undefined) {
        // 追加した発電所は選択肢に出ているので通常は到達しない
        throw new Error(t(PLANT_UNIT_UNKNOWN_ERROR_KEY))
      }
      const response = await mutateAsync({
        ...attributes,
        site:
          isNewSite && pendingSite !== undefined
            ? {
                case: 'newSite',
                value: {
                  siteName: pendingSite.siteName,
                  areaCode: pendingSite.areaCode,
                },
              }
            : { case: 'generationSiteId', value: values.siteId },
      })
      // 発電所と系統コードのエリアが違っても保存は通る。注意として知らせる
      showSiteAreaMismatchToast(t, response.siteAreaMismatch)
    },
    onClose,
    customErrorKeys: PLANT_UNIT_SAVE_ERROR_KEYS,
    unknownErrorKey: PLANT_UNIT_UNKNOWN_ERROR_KEY,
    resolveDetailedError: (err, tt, values) =>
      resolvePlantUnitSaveError(err, tt, {
        unitName: values.unitName,
        siteId: values.siteId,
        siteName: siteNameOf(values.siteId),
      }),
  })

  useLivePowerTypeOverlapCheck(control, trigger)

  // エリアを変えたら、新しいエリアに合わない発電所・系統コードの選択を外す
  const handleAreaChanged = (area: AreaCode): void => {
    const { siteId, plantMasterId } = getValues()
    const isPending = siteId === PENDING_SITE_ID
    const { clearSite, clearPlant } = selectionsToClearOnAreaChange({
      areaCode: area,
      selectedSiteArea: isPending
        ? pendingSite?.areaCode
        : sites.find((candidate) => candidate.id === siteId)?.areaCode,
      selectedPlantArea: plants.find(
        (candidate) => candidate.id === plantMasterId,
      )?.areaCode,
    })
    if (clearSite) {
      if (isPending) setPendingSite(undefined)
      setValue('siteId', '', { shouldDirty: true })
    }
    if (clearPlant) setValue('plantMasterId', '', { shouldDirty: true })
  }

  return (
    <CrudModalLayout
      handleSave={handleSave}
      isDirty={isDirty || pendingSite !== undefined}
      isOpen={isOpen}
      isPending={isPending || isSubmitting}
      onClose={handleClose}
      serverError={serverError}
      size="lg"
      title={t('page:adminPlantUnitMaster.unit.createTitle')}
    >
      <PlantUnitFormFields
        allowAddSite
        control={control}
        disabled={isPending || isSubmitting}
        errors={errors}
        idPrefix="create-unit"
        onAreaChanged={handleAreaChanged}
        onSiteAdded={(site) => {
          setPendingSite(site)
          // 追加した発電所のエリアで候補を絞り、追加した発電所を選ぶ
          setValue('areaCode', site.areaCode, { shouldDirty: true })
          setValue('siteId', PENDING_SITE_ID, {
            shouldDirty: true,
            shouldValidate: true,
          })
        }}
        pendingSite={pendingSite}
        plants={plants}
        sites={sites}
      />
    </CrudModalLayout>
  )
}
